import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDrag } from 'react-dnd';
import { useDragLayer } from 'react-dnd';
import { useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import cn from 'classnames';
import { DraftDashboardChart } from 'libs/end-to-end/src/dashboard';
import { CHART_DRAG_TYPE } from 'apps/sigma2-ui/src/pages/Dashboards/DragDropContext';
import { DragItem } from 'apps/sigma2-ui/src/pages/Dashboards/DragDropContext';
import { useDragDropContext } from 'apps/sigma2-ui/src/pages/Dashboards/DragDropContext';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { useEmbedMode } from 'apps/sigma2-ui/src/components/EmbedModeContext';

const EDGE_SIZE = 100; // px from edge before scroll starts
const SCROLL_SPEED = 3; // px per frame

interface DraggableChartCardProps {
  chart: DraftDashboardChart;
  children: React.ReactNode;
  innerStyle?: React.CSSProperties;
  dragHandleRef: React.RefObject<HTMLDivElement>;
  containerYScrollRef: React.RefObject<HTMLDivElement>;
}

interface DropPosition {
  position: 'top' | 'right' | 'bottom' | 'left';
  targetId: string;
}

// eslint-disable-next-line complexity
export const DraggableChartCard: React.FC<DraggableChartCardProps> = ({
  chart,
  children,
  innerStyle,
  dragHandleRef,
  containerYScrollRef,
}) => {
  const dropRef = useRef<HTMLDivElement>(null);

  const { onMoveChart } = useDragDropContext();
  const isEmbedMode = useEmbedMode();
  const [dropPosition, setDropPosition] = useState<DropPosition | null>(null);

  const { isDragging: draggingFromLayer, clientOffset } = useDragLayer(
    (monitor) => ({
      isDragging: monitor.isDragging(),
      clientOffset: monitor.getClientOffset(),
    }),
  );

  const { dashboardDraft } = useDashboards();
  const charts = useMemo(
    () => dashboardDraft?.draft.charts ?? [],
    [dashboardDraft?.draft.charts],
  );

  // Override behavior when user drags chart near a vertical edge of the screen to scroll the page.
  useEffect(() => {
    let frame: number;
    function scrollIfNeeded() {
      if (draggingFromLayer && clientOffset && containerYScrollRef.current) {
        const rect = containerYScrollRef.current.getBoundingClientRect();
        const { y } = clientOffset;

        // Scroll up
        if (y < rect.top + EDGE_SIZE) {
          containerYScrollRef.current.scrollTop -= SCROLL_SPEED;
        }
        // Scroll down
        else if (y > rect.bottom - EDGE_SIZE) {
          containerYScrollRef.current.scrollTop += SCROLL_SPEED;
        }
      }
      frame = requestAnimationFrame(scrollIfNeeded);
    }
    frame = requestAnimationFrame(scrollIfNeeded);
    return () => cancelAnimationFrame(frame);
  }, [draggingFromLayer, clientOffset, containerYScrollRef]);

  // Helper function to check if dropping would exceed the 3-chart limit
  const wouldExceedRowLimit = useCallback(
    (
      draggedChartDraftId: string,
      position: 'top' | 'right' | 'bottom' | 'left',
    ): boolean => {
      if (!dashboardDraft?.draft) return false;

      // For left/right drops, we're adding to the same row
      if (position === 'left' || position === 'right') {
        const targetRow = chart.positionOnDashboard;
        const chartsInRow = charts.filter(
          (c) => c.positionOnDashboard === targetRow,
        );
        // If the dragged chart is already in this row, we're not adding a new chart
        const draggedChartInRow = chartsInRow.find(
          (c) => c.draftId === draggedChartDraftId,
        );

        if (draggedChartInRow) {
          return false; // Already in this row, so no change in count
        }
        return chartsInRow.length >= 3;
      }

      // For top/bottom drops, we're creating a new row or adding to an existing row
      // This is more complex and would require checking the actual move logic
      // For now, we'll be conservative and allow these moves
      return false;
    },
    [dashboardDraft?.draft, chart.positionOnDashboard, charts],
  );

  const [{ isDragging }, drag, preview] = useDrag({
    type: CHART_DRAG_TYPE,
    canDrag: !isEmbedMode,
    item: (): DragItem => ({ id: chart.draftId, chart }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      setDropPosition(null);
    },
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: CHART_DRAG_TYPE,
    canDrop: () => !isEmbedMode,
    hover: (item: DragItem, monitor) => {
      if (!dropRef.current || item.id === chart.draftId) {
        return;
      }

      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      // Calculate relative position within the card
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Calculate distances to each edge
      const distanceToTop = hoverClientY;
      const distanceToBottom = hoverBoundingRect.height - hoverClientY;
      const distanceToLeft = hoverClientX;
      const distanceToRight = hoverBoundingRect.width - hoverClientX;

      // Find the closest edge
      const minDistance = Math.min(
        distanceToTop,
        distanceToRight,
        distanceToBottom,
        distanceToLeft,
      );

      let position: 'top' | 'right' | 'bottom' | 'left';
      if (minDistance === distanceToTop) {
        position = 'top';
      } else if (minDistance === distanceToRight) {
        position = 'right';
      } else if (minDistance === distanceToBottom) {
        position = 'bottom';
      } else {
        position = 'left';
      }

      // Check if this drop would exceed the row limit
      if (wouldExceedRowLimit(item.id, position)) {
        setDropPosition(null);
        return;
      }

      setDropPosition({ position, targetId: chart.draftId });
    },
    drop: async (item: DragItem) => {
      if (!dropPosition || item.id === chart.draftId) {
        return;
      }

      // Double-check the row limit before actually dropping
      if (wouldExceedRowLimit(item.id, dropPosition.position)) {
        setDropPosition(null);
        return;
      }

      await onMoveChart(item.id, dropPosition.targetId, dropPosition.position);

      setDropPosition(null);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // Use empty image as drag preview to create custom preview
  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Apply drop to the main container and drag to the handle
  drop(dropRef);
  drag(dragHandleRef);

  const showDropIndicator = isOver && canDrop && dropPosition;

  return (
    <div
      className="relative transition-all duration-100 ease-out h-full"
      style={{
        ...innerStyle,
        // Enable smooth layout animations
        willChange: 'transform',
      }}
    >
      {/* Drop indicator - top */}
      {showDropIndicator && dropPosition.position === 'top' && (
        <div className="absolute -top-2 left-[2%] w-[96%] h-1 bg-blue-500 rounded z-30" />
      )}

      {/* Drop indicator - left */}
      {showDropIndicator && dropPosition.position === 'left' && (
        <div className="absolute -left-2 h-[96%] top-[2%] w-1 bg-blue-500 rounded z-30" />
      )}

      <div
        ref={dropRef}
        className={cn('h-full', {
          'opacity-50': isDragging,
        })}
      >
        {children}
      </div>

      {/* Drop indicator - right */}
      {showDropIndicator && dropPosition.position === 'right' && (
        <div className="absolute -right-2 h-[96%] top-[2%] w-1 bg-blue-500 rounded z-30" />
      )}

      {/* Drop indicator - bottom */}
      {showDropIndicator && dropPosition.position === 'bottom' && (
        <div className="absolute -bottom-2 left-[2%] w-[96%] h-1 bg-blue-500 rounded z-30" />
      )}
    </div>
  );
};
